import {
  DashboardWidgetInstance,
  WidgetTemplate,
  StepWidgetConfigs,
} from '../types';
import { widgetRegistry, isInputWidget } from '../components/widgets/registry';
import type { WidgetType } from '../components/widgets/types';

// Validation result for a single widget
export interface WidgetValidationResult {
  isValid: boolean;
  widgetId: string;
  widgetTitle: string;
  widgetType: string;
  errors: string[];
}

// Overall validation result for a step
export interface StepValidationResult {
  isValid: boolean;
  widgets: WidgetValidationResult[];
  errorCount: number;
}

/**
 * Validates all input widgets in a dashboard step.
 * Delegates to each widget's own `validate` function from the registry.
 */
export function validateStepWidgets(
  dashboardData: Record<string, unknown>,
  dashboardWidgets: DashboardWidgetInstance[],
  widgetTemplates: Map<string, WidgetTemplate>,
  stepConfigs?: StepWidgetConfigs
): StepValidationResult {
  const widgetResults: WidgetValidationResult[] = [];

  for (const widgetInstance of dashboardWidgets) {
    const template = widgetTemplates.get(widgetInstance.templateId);
    if (!template) continue;

    // Only validate input widgets
    if (!isInputWidget(template.type as WidgetType)) continue;

    const widgetTitle = widgetInstance.title || template.name;

    // Get the dataKey — must match the priority used by the renderer:
    // 1. Dashboard-level configOverrides.dataKey (preferred)
    // 2. Legacy step-level widgetConfigs dataKey (fallback)
    // 3. Auto-generated key (auto_<widgetId>)
    let dataKey: string | undefined;
    const dashDk = widgetInstance.configOverrides?.dataKey;
    if (typeof dashDk === 'string' && dashDk) {
      dataKey = dashDk;
    } else if (stepConfigs?.widgets?.[widgetInstance.id]?.dataKey) {
      dataKey = stepConfigs.widgets[widgetInstance.id].dataKey;
    }

    // Fall back to auto-generated dataKey (same as renderer)
    if (!dataKey) {
      const autoKey = `auto_${widgetInstance.id}`;
      if (dashboardData[autoKey] !== undefined) {
        dataKey = autoKey;
      }
    }

    if (!dataKey) continue;

    // Look up the widget's validate function from the registry
    const definition = widgetRegistry.get(template.type as WidgetType);
    if (!definition?.validate) continue;

    const errors = definition.validate(dashboardData[dataKey]);
    if (errors.length > 0) {
      widgetResults.push({
        isValid: false,
        widgetId: widgetInstance.id,
        widgetTitle,
        widgetType: template.type,
        errors,
      });
    }
  }

  const errorCount = widgetResults.reduce((count, r) => count + r.errors.length, 0);

  return {
    isValid: widgetResults.length === 0,
    widgets: widgetResults,
    errorCount,
  };
}

/**
 * Get a summary message for validation errors
 */
export function getValidationSummary(result: StepValidationResult): string {
  if (result.isValid) {
    return '';
  }

  const widgetCount = result.widgets.length;
  const errorCount = result.errorCount;

  if (widgetCount === 1) {
    return `${errorCount} required field${errorCount > 1 ? 's' : ''} need${errorCount === 1 ? 's' : ''} to be completed in "${result.widgets[0].widgetTitle}"`;
  }

  return `${errorCount} required field${errorCount > 1 ? 's' : ''} need${errorCount === 1 ? 's' : ''} to be completed across ${widgetCount} widgets`;
}
